import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { radius } from "src/design/tokens";
import { InlineAlert } from "src/grading/components/InlineAlert/InlineAlert";
import LoadingButton from "src/grading/components/LoadingButton/LoadingButton";
import { SourceBadge } from "src/grading/components/SourceBadge/SourceBadge";
import {
  messageOf,
  refusalCode,
  refusalCopy,
} from "src/grading/utils/apiError";
import { ApiError, BenchmarkSex } from "src/models";
import { AddStudentPayload } from "src/models/hierarchy";
import {
  STUDENT_GRADES,
  StudentSummary,
  studentName,
} from "src/models/students";
import {
  useAddStudentMutation,
  useEnrollStudentMutation,
} from "src/store/reducers/classes/classesApiSlice";

export interface AddStudentDialogProps {
  open: boolean;
  onClose: () => void;
  /** The class the student joins. Without it the dialog asks for one of `classes`. */
  classId?: string;
  /** Classes to choose from when `classId` is not fixed (a teacher's own classes). */
  classes?: { id: string; name: string }[];
  /**
   * A searchable class field, used instead of the `classes` select when the choice is too large
   * to list (an admin's whole scope). It reports the picked class id, or null.
   */
  renderClassPicker?: (onChange: (classId: string | null) => void) => ReactNode;
  /** The class's grade, used as the default grade. */
  defaultGrade?: string | null;
  /** Called with the name of the student who was added or enrolled. */
  onAdded?: (name: string) => void;
}

const emptyForm = (grade?: string | null) => ({
  firstName: "",
  lastName: "",
  benchmarkSex: "" as BenchmarkSex | "",
  ageOverride: "",
  gradeLevel: grade ?? "",
  externalId: "",
});

/**
 * Adds a student by hand with only the allowed fields (FR-027). A likely duplicate at the school
 * (409 `POSSIBLE_DUPLICATE`) lists the existing students with "Enroll {name} instead", or the
 * viewer can create a new record anyway (FR-024).
 */
export const AddStudentDialog = ({
  open,
  onClose,
  classId: fixedClassId,
  classes,
  renderClassPicker,
  defaultGrade,
  onAdded,
}: AddStudentDialogProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [addStudent, { isLoading: adding }] = useAddStudentMutation();
  const [enrollStudent, { isLoading: enrolling }] = useEnrollStudentMutation();
  const [form, setForm] = useState(() => emptyForm(defaultGrade));
  const [pickedClass, setPickedClass] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<StudentSummary[] | null>(null);

  // Read by the reset effect without being a dependency: the class list may re-render as a new
  // array, and that must not wipe what the viewer typed.
  const classesRef = useRef(classes);
  classesRef.current = classes;
  useEffect(() => {
    if (!open) return;
    const options = classesRef.current;
    setForm(emptyForm(defaultGrade));
    setPickedClass(options?.length === 1 ? options[0].id : "");
    setError(null);
    setCandidates(null);
  }, [open, defaultGrade]);

  const classId = fixedClassId ?? pickedClass;
  const body: AddStudentPayload = {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    benchmarkSex: form.benchmarkSex || undefined,
    ageOverride: form.ageOverride ? Number(form.ageOverride) : undefined,
    gradeLevel: form.gradeLevel || undefined,
    externalId: form.externalId.trim() || undefined,
  };
  const name = `${body.firstName} ${body.lastName}`.trim();

  const finish = (added: string) => {
    onAdded?.(added);
    onClose();
  };

  const submit = async (confirmNew = false) => {
    if (!classId) return;
    setError(null);
    try {
      await addStudent({
        classId,
        body: confirmNew ? { ...body, confirmNew: true } : body,
      }).unwrap();
      finish(name);
    } catch (e) {
      if (refusalCode(e) === "POSSIBLE_DUPLICATE") {
        const found = (e as ApiError).data?.data?.candidates;
        setCandidates(Array.isArray(found) ? (found as StudentSummary[]) : []);
        return;
      }
      setError(messageOf(e, t("classes.addStudentError")));
    }
  };

  const enrollInstead = async (candidate: StudentSummary) => {
    setError(null);
    try {
      await enrollStudent({ studentId: candidate.id, classId }).unwrap();
      finish(studentName(candidate));
    } catch (e) {
      const code = refusalCode(e);
      setError(
        code
          ? refusalCopy(t, code)
          : messageOf(e, t("student.add.enrollError")),
      );
    }
  };

  const busy = adding || enrolling;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId} component="h2">
        {t("classes.addStudent")}
      </DialogTitle>
      <DialogContent
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1.5,
          pt: 1,
          "& > .full": { gridColumn: "1 / -1" },
        }}
      >
        <Typography className="full" variant="body2" color="text.secondary">
          {t("classes.addStudentStored")}
        </Typography>
        {fixedClassId ? null : renderClassPicker ? (
          <Box className="full">
            {renderClassPicker((id) => setPickedClass(id ?? ""))}
          </Box>
        ) : (
          <TextField
            className="full"
            select
            label={t("student.add.class")}
            value={pickedClass}
            onChange={(e) => setPickedClass(e.target.value)}
            required
          >
            {(classes ?? []).map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          label={t("classes.firstName")}
          value={form.firstName}
          onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          required
          autoFocus
          inputProps={{ maxLength: 80 }}
        />
        <TextField
          label={t("classes.lastName")}
          value={form.lastName}
          onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          required
          inputProps={{ maxLength: 80 }}
        />
        <TextField
          select
          label={t("classes.benchmarkTable")}
          value={form.benchmarkSex}
          onChange={(e) =>
            setForm({
              ...form,
              benchmarkSex: e.target.value as BenchmarkSex | "",
            })
          }
        >
          <MenuItem value="">{t("review.notSet")}</MenuItem>
          <MenuItem value="Male">{t("classes.sexMale")}</MenuItem>
          <MenuItem value="Female">{t("classes.sexFemale")}</MenuItem>
        </TextField>
        <TextField
          label={t("classes.age")}
          type="number"
          value={form.ageOverride}
          onChange={(e) => setForm({ ...form, ageOverride: e.target.value })}
          required
          inputProps={{ min: 5, max: 19 }}
          helperText={t("classes.ageHelper")}
        />
        <TextField
          select
          label={t("classes.grade")}
          value={form.gradeLevel}
          onChange={(e) => setForm({ ...form, gradeLevel: e.target.value })}
        >
          <MenuItem value="">{t("review.notSet")}</MenuItem>
          {STUDENT_GRADES.map((g) => (
            <MenuItem key={g} value={g}>
              {g}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t("classes.sisIdOptional")}
          value={form.externalId}
          onChange={(e) => setForm({ ...form, externalId: e.target.value })}
          inputProps={{ maxLength: 100 }}
        />
        {candidates ? (
          <Box
            className="full"
            component="section"
            aria-labelledby={`${titleId}-duplicate`}
            sx={(theme) => ({
              p: 2,
              borderRadius: `${radius.panel}px`,
              bgcolor: theme.palette.tokens.warningBg,
              color: theme.palette.tokens.warningFg,
            })}
          >
            <Typography
              id={`${titleId}-duplicate`}
              component="h3"
              sx={{ fontWeight: 800, fontSize: 15, mb: 0.5 }}
            >
              {t("student.add.duplicateTitle")}
            </Typography>
            <Typography sx={{ fontSize: 14, mb: 1.5 }}>
              {t("student.add.duplicateBody")}
            </Typography>
            <Stack
              component="ul"
              spacing={1}
              sx={{ m: 0, p: 0, listStyle: "none" }}
            >
              {candidates.map((candidate) => (
                <Box
                  component="li"
                  key={candidate.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ fontWeight: 700 }}>{studentName(candidate)}</Box>
                    <Box sx={{ fontSize: 13 }}>
                      {t("student.add.candidateLine", {
                        grade: candidate.gradeLevel
                          ? t("classes.gradeLabel", {
                              grade: candidate.gradeLevel,
                            })
                          : t("review.notSet"),
                        sis: candidate.externalId || "—",
                      })}
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SourceBadge source={candidate.source} />
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busy}
                      onClick={() => enrollInstead(candidate)}
                    >
                      {t("student.add.enrollInstead", {
                        name: studentName(candidate),
                      })}
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        ) : null}
        {error ? (
          <Box className="full">
            <InlineAlert severity="error">{error}</InlineAlert>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={busy}>
          {t("common.cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          loading={adding}
          disabled={
            !body.firstName || !body.lastName || !form.ageOverride || !classId
          }
          onClick={() => submit(candidates !== null)}
          label={
            candidates ? t("student.add.createAnyway") : t("classes.addStudent")
          }
        />
      </DialogActions>
    </Dialog>
  );
};

export default AddStudentDialog;
